import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { ArrowLeft, Search, ChevronRight, Scissors, Droplets, Wind, Shirt } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CategoryPage() {
  const { id } = useParams();
  const { t, lang, isRTL } = useLanguage();
  const [category, setCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/categories/${id}`),
      axios.get(`${API}/subcategories?category_id=${id}&active_only=true`),
      axios.get(`${API}/products?category_id=${id}&active_only=true`)
    ])
      .then(([catRes, subRes, prodRes]) => {
        setCategory(catRes.data);
        setSubcategories(subRes.data);
        setProducts(prodRes.data);
        setFilteredProducts(prodRes.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    let result = products;
    
    // Filter by subcategory
    if (selectedSubcategory) {
      result = result.filter(p => p.subcategory_id === selectedSubcategory);
    }
    
    // Filter by search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        p.name_en.toLowerCase().includes(q) || p.name_ar.includes(q)
      );
    }
    
    // Filter by service type
    if (serviceFilter !== 'all') {
      result = result.filter(p => p.service_types?.includes(serviceFilter));
    }
    
    setFilteredProducts(result);
  }, [search, serviceFilter, products, selectedSubcategory]);

  const getName = (item) => lang === 'ar' ? item.name_ar : item.name_en;
  
  const formatPrice = (price) => {
    if (!price || price <= 0) return null;
    return `${Number(price).toFixed(2)} ${t('sar')}`;
  };
  
  const getServiceLabel = (type) => {
    const labels = { 
      regular_wash: t('regularWash'), 
      dry_clean: t('dryClean'), 
      pressing: t('pressing') 
    };
    return labels[type] || type;
  };

  const getServiceIcon = (type) => {
    switch(type) {
      case 'regular_wash': return <Droplets className="h-3 w-3" />;
      case 'dry_clean': return <Wind className="h-3 w-3" />;
      case 'pressing': return <Scissors className="h-3 w-3" />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#0056A6] border-t-transparent" />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <p className="text-slate-500">{t('noCategories')}</p>
        <Link to="/">
          <Button variant="outline" className="mt-4">{t('backToMenu')}</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="fade-in" data-testid="category-page">
      {/* Category Header */}
      <div className="bg-gradient-to-br from-[#0056A6] to-[#003d75] py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-sm mb-4 transition-colors" 
            data-testid="back-link"
          >
            <ArrowLeft className={`h-4 w-4 ${isRTL ? 'rotate-180' : ''}`} />
            {t('backToMenu')}
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            {getName(category)}
          </h1>
          <p className="text-white/60">
            {subcategories.length} {t('subcategories')} • {products.length} {t('items')}
          </p>
        </div>
      </div>

      {/* Subcategories Tabs */}
      {subcategories.length > 0 && (
        <div className="border-b border-slate-100 bg-white sticky top-16 z-20">
          <div className="container mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex gap-1 overflow-x-auto py-3 scrollbar-hide">
              <button
                onClick={() => setSelectedSubcategory(null)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  !selectedSubcategory 
                    ? 'bg-[#0056A6] text-white shadow-sm' 
                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                }`}
                data-testid="subcategory-all"
              >
                {t('allServices')} ({products.length})
              </button>
              {subcategories.map(sub => (
                <button
                  key={sub.id}
                  onClick={() => setSelectedSubcategory(sub.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    selectedSubcategory === sub.id 
                      ? 'bg-[#0056A6] text-white shadow-sm' 
                      : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                  }`}
                  data-testid={`subcategory-${sub.id}`}
                >
                  {getName(sub)} ({sub.product_count || 0})
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-6">
        <div className="flex flex-col sm:flex-row gap-3 max-w-2xl">
          <div className="relative flex-1">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder={t('searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="ps-10 h-10"
              data-testid="category-search-input"
            />
          </div>
          <select 
            value={serviceFilter} 
            onChange={(e) => setServiceFilter(e.target.value)}
            className="flex h-10 w-full sm:w-48 items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            data-testid="service-filter"
          >
            <option value="all">{t('allServices')}</option>
            <option value="regular_wash">{t('regularWash')}</option>
            <option value="dry_clean">{t('dryClean')}</option>
            <option value="pressing">{t('pressing')}</option>
          </select>
        </div>
      </div>

      {/* Products Grid */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 pb-16">
        {filteredProducts.length === 0 ? (
          <div className="text-center py-16">
            <Shirt className="h-12 w-12 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-400">{t('noProducts')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProducts.map((product, idx) => (
              <div
                key={product.id}
                className={`product-card bg-white rounded-xl border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden slide-up stagger-${Math.min(idx + 1, 6)} hover:shadow-md hover:border-slate-200 transition-all`}
                data-testid={`product-card-${product.id}`}
              >
                <div className="p-5">
                  {/* Product Header */}
                  <div className="flex gap-4 mb-4">
                    {product.image ? (
                      <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
                        <img src={product.image} alt={getName(product)} className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#0056A6]/10 to-[#0056A6]/5 flex items-center justify-center flex-shrink-0">
                        <Scissors className="h-6 w-6 text-[#0056A6]/60" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-800 text-base mb-1">{getName(product)}</h3>
                      <div className="flex gap-1.5 flex-wrap">
                        {product.service_types?.map(type => (
                          <Badge 
                            key={type} 
                            variant="secondary" 
                            className="text-[0.65rem] bg-slate-50 text-slate-500 border-0 rounded-md flex items-center gap-1"
                          >
                            {getServiceIcon(type)}
                            {getServiceLabel(type)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Prices Grid */}
                  <div className="grid grid-cols-3 gap-3 pt-4 border-t border-slate-50">
                    {/* Regular Wash */}
                    <div className={`text-center p-2 rounded-lg ${product.laundry_price > 0 ? 'bg-blue-50' : 'bg-slate-50 opacity-40'}`}>
                      <div className="flex items-center justify-center mb-1">
                        <Droplets className={`h-4 w-4 ${product.laundry_price > 0 ? 'text-blue-500' : 'text-slate-300'}`} />
                      </div>
                      <p className="text-[0.65rem] text-slate-500 mb-0.5">{t('regularWash')}</p>
                      <p className={`text-sm font-bold ${product.laundry_price > 0 ? 'text-[#0056A6]' : 'text-slate-300'}`}>
                        {formatPrice(product.laundry_price) || '-'}
                      </p>
                    </div>
                    
                    {/* Dry Clean */}
                    <div className={`text-center p-2 rounded-lg ${product.dry_clean_price > 0 ? 'bg-emerald-50' : 'bg-slate-50 opacity-40'}`}>
                      <div className="flex items-center justify-center mb-1">
                        <Wind className={`h-4 w-4 ${product.dry_clean_price > 0 ? 'text-emerald-500' : 'text-slate-300'}`} />
                      </div>
                      <p className="text-[0.65rem] text-slate-500 mb-0.5">{t('dryClean')}</p>
                      <p className={`text-sm font-bold ${product.dry_clean_price > 0 ? 'text-emerald-600' : 'text-slate-300'}`}>
                        {formatPrice(product.dry_clean_price) || '-'}
                      </p>
                    </div>
                    
                    {/* Pressing */}
                    <div className={`text-center p-2 rounded-lg ${product.pressing_price > 0 ? 'bg-amber-50' : 'bg-slate-50 opacity-40'}`}>
                      <div className="flex items-center justify-center mb-1">
                        <Scissors className={`h-4 w-4 ${product.pressing_price > 0 ? 'text-amber-500' : 'text-slate-300'}`} />
                      </div>
                      <p className="text-[0.65rem] text-slate-500 mb-0.5">{t('pressing')}</p>
                      <p className={`text-sm font-bold ${product.pressing_price > 0 ? 'text-amber-600' : 'text-slate-300'}`}>
                        {formatPrice(product.pressing_price) || '-'}
                      </p>
                    </div>
                  </div>
                  
                  {/* VAT Note */}
                  <p className="text-[0.6rem] text-slate-300 text-center mt-2">{t('priceIncVat')}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
